<template>
	<!-- Minimized Badge -->
	<div
		v-if="isVisible && isMinimized"
		@click="handleMaximize"
		:class="['kai-minimized-badge', { 'has-drawer-badge': drawerOpen && drawerMinimized }]"
		title="Click to open KAi Assistant"
		k-data-id="aiAgent-header-minimizedBadge">
		<span class="kai-badge-text">KAi</span>
	</div>

	<!-- Full Modal -->
	<n-modal
		v-model:show="isVisible"
		v-if="!isMinimized"
		:mask-closable="true"
		preset="card"
		title="KAi Assistant"
		:style="{ width: '90%', maxWidth: '800px', maxHeight: '80vh' }"
		:segmented="{ content: true }"
		:closable="true"
		k-data-id="aiAgent-main-container">
		<template #header>
			<div class="flex items-center gap-3">
				<span class="text-xl font-semibold">KAi Assistant</span>
				<n-badge
					v-if="selectedMode"
					:value="selectedMode === 'help' ? '📚 Help' : '🔍 Search'"
					type="success" />
				<n-badge v-if="messages.length > 0" :value="messages.length" :max="99" type="info" />
			</div>
		</template>
		<template #header-extra>
			<n-button
				text
				size="small"
				@click="handleMinimize"
				title="Minimize"
				class="mr-2"
				k-data-id="aiAgent-header-minimizeButton">
				<span class="text-xl">−</span>
			</n-button>
		</template>

		<div class="flex flex-col" style="height: 60vh" k-data-id="aiAgent-chat-window">
			<div
				class="flex-1 overflow-y-auto p-4 space-y-4"
				ref="messagesContainer"
				k-data-id="aiAgent-messages-container">
				<!-- Mode Selection (shown when no messages and no mode selected) -->
				<div
					v-if="messages.length === 0 && !selectedMode"
					class="text-center text-theme-secondary mt-8"
					k-data-id="aiAgent-modeSelection-container">
					<p class="text-xl mb-6">👋 Hello! How can I help you today?</p>

					<div class="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
						<!-- Help Mode -->
						<n-card
							hoverable
							class="cursor-pointer transition-all"
							@click="selectMode('help')"
							k-data-id="aiAgent-modeSelection-helpCard">
							<div class="text-center p-4">
								<div class="text-4xl mb-3">📚</div>
								<h3 class="text-lg font-semibold mb-2">Help</h3>
								<p class="text-sm text-theme-muted">
									Ask questions from our documentation to understand features
								</p>
							</div>
						</n-card>

						<!-- Search Mode -->
						<n-card
							hoverable
							class="cursor-pointer transition-all"
							@click="selectMode('search')"
							k-data-id="aiAgent-modeSelection-searchCard">
							<div class="text-center p-4">
								<div class="text-4xl mb-3">🔍</div>
								<h3 class="text-lg font-semibold mb-2">Search</h3>
								<p class="text-sm text-theme-muted">
									Search the platform to find users/courses and interact with specific data
								</p>
							</div>
						</n-card>
					</div>
				</div>

				<!-- Mode-Specific Examples (shown after mode selected but before first message) -->
				<div
					v-if="messages.length === 0 && selectedMode"
					class="text-center text-theme-secondary mt-8"
					k-data-id="aiAgent-suggestions-container">
					<div class="mb-6">
						<n-button
							text
							@click="store.dispatch('aiAgent/setSelectedMode', null)"
							class="text-sm"
							k-data-id="aiAgent-suggestions-backButton">
							← Back to mode selection
						</n-button>
					</div>

					<div
						v-if="selectedMode === 'help'"
						class="space-y-3"
						k-data-id="aiAgent-suggestions-helpMode">
						<p class="text-lg font-semibold mb-4">💡 Ask me about features:</p>
						<div class="flex flex-col gap-2 max-w-md mx-auto">
							<n-button
								secondary
								size="large"
								@click="handleSuggestionClick('How do I create a course')"
								k-data-id="aiAgent-suggestions-helpCourse">
								How do I create a course?
							</n-button>
							<n-button
								secondary
								size="large"
								@click="handleSuggestionClick('Where do I find notifications')"
								k-data-id="aiAgent-suggestions-helpNotifications">
								Where do I find notifications?
							</n-button>
							<n-button
								secondary
								size="large"
								@click="handleSuggestionClick('What does user limit mean on access')"
								k-data-id="aiAgent-suggestions-helpUserLimit">
								What does user limit mean on access?
							</n-button>
						</div>
					</div>

					<div
						v-if="selectedMode === 'search'"
						class="space-y-3"
						k-data-id="aiAgent-suggestions-searchMode">
						<p class="text-lg font-semibold mb-4">🔍 Try searching:</p>
						<div class="flex flex-col gap-2 max-w-md mx-auto">
							<n-button
								secondary
								size="large"
								@click="handlePromptSuggestion('Find user ')"
								k-data-id="aiAgent-suggestions-searchUser">
								Find user
							</n-button>
							<n-button
								secondary
								size="large"
								@click="handlePromptSuggestion('Search for courses about ')"
								k-data-id="aiAgent-suggestions-searchCourses">
								Search for courses
							</n-button>
							<n-button
								secondary
								size="large"
								@click="handleSuggestionClick('List recent users')"
								k-data-id="aiAgent-suggestions-searchRecentUsers">
								List recent users
							</n-button>
						</div>
					</div>
				</div>

				<div v-for="message in messages" :key="message.id" class="message-item">
					<div
						v-if="message.type === 'user'"
						class="flex justify-end mb-4"
						k-data-id="aiAgent-message-user">
						<div
							class="bg-theme-tertiary text-theme-primary rounded-2xl px-5 py-3 max-w-[75%] shadow-sm">
							<p class="text-sm whitespace-pre-wrap">{{ message.content }}</p>
						</div>
					</div>

					<div
						v-else-if="message.type === 'agent'"
						class="flex justify-start mb-4 group"
						k-data-id="aiAgent-message-agent">
						<div class="bg-theme-secondary rounded-2xl px-5 py-4 max-w-[75%] shadow-sm relative">
							<!-- Pin button (shows on hover) -->
							<n-button
								text
								size="tiny"
								class="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
								@click="handlePinMessage(message)"
								title="Pin to drawer"
								k-data-id="aiAgent-message-pinButton">
								<span class="text-lg">📌</span>
							</n-button>

							<div
								class="text-sm mb-2 whitespace-pre-wrap linkified-content"
								v-html="linkifyContent(message.content)"></div>

							<!-- Display specific field value -->
							<div v-if="message.fieldData" class="mt-3">
								<n-card size="small" class="mb-2 bg-theme-primary">
									<div class="py-2">
										<p class="text-xs text-theme-muted mb-1">{{ message.fieldData.field }}</p>

										<!-- Display image if it's an image field -->
										<div v-if="message.fieldData.isImage" class="mt-2">
											<img
												:src="message.fieldData.rawValue"
												:alt="message.fieldData.field"
												class="max-w-full h-auto rounded-lg shadow-md"
												style="max-height: 300px; object-fit: contain"
												@error="(e) => (e.target.style.display = 'none')" />
											<p class="text-xs text-theme-muted mt-2 break-all">
												{{ message.fieldData.rawValue }}
											</p>
										</div>

										<!-- Display formatted timestamp -->
										<div v-else-if="message.fieldData.isTimestamp" class="mt-1">
											<p class="font-semibold text-base text-theme-primary">
												{{ message.fieldData.value }}
											</p>
											<p class="text-xs text-theme-muted mt-1">
												Raw: {{ JSON.stringify(message.fieldData.rawValue) }}
											</p>
										</div>

										<!-- Display regular value -->
										<p
											v-else
											class="font-semibold text-base text-theme-primary break-all whitespace-pre-wrap">
											{{
												typeof message.fieldData.value === 'object'
													? JSON.stringify(message.fieldData.value, null, 2)
													: message.fieldData.value
											}}
										</p>
									</div>
								</n-card>
							</div>

							<!-- Display formatted entity data (dynamic) -->
							<div v-if="message.data && message.formatted && !message.fieldData" class="mt-3">
								<p class="text-xs text-theme-muted mb-2">
									💡 Hover over {{ getArticle(message.formatted) }}
									{{ getEntityName(message.formatted) }} to see actions
								</p>
								<n-card size="small" class="mb-2 bg-theme-primary">
									<div v-if="Array.isArray(message.formatted)">
										<div
											v-for="(item, idx) in message.formatted"
											:key="idx"
											class="border-b last:border-b-0 py-3 px-3 -mx-2 relative group">
											<div class="flex justify-between items-start">
												<div class="flex-1">
													<div
														v-for="field in getDisplayFields(item)"
														:key="field.key"
														:class="getFieldClass(field)">
														{{ field.prefix || '' }}{{ item[field.key] }}
													</div>
												</div>
												<div
													class="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
													<n-button
														v-for="action in getEntityActions(item)"
														:key="action.label"
														:type="action.primary ? 'primary' : 'default'"
														size="tiny"
														@click.stop="handleEntityAction(action, item)">
														<span v-if="action.icon">{{ action.icon }}</span>
														{{ action.label }}
													</n-button>
												</div>
											</div>
										</div>
									</div>
									<div v-else class="rounded px-3 py-2 -mx-2 -my-1 relative group">
										<div class="flex justify-between items-start">
											<div class="flex-1">
												<div
													v-for="field in getDisplayFields(message.formatted)"
													:key="field.key"
													:class="getFieldClass(field)">
													{{ field.prefix || '' }}{{ message.formatted[field.key] }}
												</div>
											</div>
											<div class="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
												<n-button
													v-for="action in getEntityActions(message.formatted)"
													:key="action.label"
													:type="action.primary ? 'primary' : 'default'"
													size="tiny"
													@click.stop="handleEntityAction(action, message.formatted)">
													<span v-if="action.icon">{{ action.icon }}</span>
													{{ action.label }}
												</n-button>
											</div>
										</div>
									</div>
								</n-card>
							</div>

							<div v-if="message.suggestions && message.suggestions.length > 0" class="mt-3">
								<p class="text-xs text-theme-secondary mb-2">Suggestions:</p>
								<div class="flex flex-wrap gap-2">
									<n-button
										v-for="(suggestion, idx) in message.suggestions"
										:key="idx"
										size="tiny"
										secondary
										round
										@click="handleSuggestionClick(suggestion)">
										{{ suggestion }}
									</n-button>
								</div>
							</div>
						</div>
					</div>

					<div
						v-else-if="message.type === 'system'"
						class="flex justify-center mb-4"
						k-data-id="aiAgent-message-system">
						<div class="bg-warning text-dark rounded-full px-4 py-1 shadow-sm">
							<p class="text-xs">{{ message.content }}</p>
						</div>
					</div>
				</div>

				<div
					v-if="isProcessing"
					class="flex justify-start mb-4"
					k-data-id="aiAgent-message-processing">
					<div class="bg-theme-secondary rounded-2xl px-5 py-4 shadow-sm">
						<div class="flex items-center gap-3">
							<n-spin size="small" />
							<span class="text-sm text-theme-secondary">Thinking...</span>
						</div>
					</div>
				</div>
			</div>

			<!-- Input Area - Only shown after mode is selected OR if messages already exist -->
			<div
				v-if="selectedMode || messages.length > 0"
				class="border-t border-theme-primary p-4 bg-theme-secondary"
				k-data-id="aiAgent-input-area">
				<!-- Selected Entity Indicator (dynamic) -->
				<div
					v-if="selectedEntity"
					class="mb-3 bg-theme-tertiary rounded-lg p-3 flex justify-between items-center"
					k-data-id="aiAgent-input-selectedEntity">
					<div>
						<p class="text-xs text-theme-muted mb-1 capitalize">
							Selected {{ selectedEntity._displayConfig?.entityName || 'item' }}:
						</p>
						<div
							v-for="field in getDisplayFields(selectedEntity)"
							:key="field.key"
							:class="getFieldClass(field, true)">
							{{ field.prefix || '' }}{{ selectedEntity[field.key] }}
						</div>
					</div>
					<n-button
						text
						size="small"
						@click="clearSelectedEntity"
						k-data-id="aiAgent-input-clearEntityButton">
						<span class="text-xs">✕ Clear</span>
					</n-button>
				</div>

				<div class="flex gap-3">
					<n-input
						v-model:value="inputMessage"
						type="textarea"
						:placeholder="getInputPlaceholder()"
						:autosize="{ minRows: 1, maxRows: 3 }"
						@keydown.enter.prevent="handleEnterKey"
						:disabled="isProcessing"
						class="flex-1"
						k-data-id="aiAgent-input-textField" />
					<n-button
						type="primary"
						size="large"
						@click="sendMessage"
						:disabled="!inputMessage.trim() || isProcessing"
						:loading="isProcessing"
						k-data-id="aiAgent-input-sendButton">
						Send
					</n-button>
				</div>
				<div class="flex justify-between items-center mt-2 px-1">
					<div class="flex gap-2">
						<n-button text size="tiny" @click="clearChat" k-data-id="aiAgent-input-clearChatButton">
							<span class="text-xs">Clear chat</span>
						</n-button>
						<n-dropdown trigger="click" :options="copyOptions" :disabled="messages.length === 0">
							<n-button
								text
								size="tiny"
								:disabled="messages.length === 0"
								k-data-id="aiAgent-input-copyChatButton">
								<span class="text-xs">Copy chat</span>
							</n-button>
						</n-dropdown>
					</div>
					<span class="text-xs text-theme-muted" k-data-id="aiAgent-input-messageCount">
						{{ messages.length }} message{{ messages.length !== 1 ? 's' : '' }}
					</span>
				</div>
			</div>
		</div>
	</n-modal>

	<!-- Minimized Drawer Badge -->
	<div
		v-if="drawerOpen && drawerMinimized"
		@click="handleDrawerMaximize"
		:class="['kai-drawer-minimized-badge', { 'has-kai-badge': isVisible && isMinimized }]"
		title="Click to open Pinned Instructions">
		<PhInfo :size="16" weight="fill" class="text-white" />
	</div>

	<!-- Pinned Message Drawer (resizable, no mask) -->
	<n-drawer
		v-model:show="drawerOpen"
		v-if="!drawerMinimized"
		:width="drawerWidth"
		:placement="drawerPlacement"
		:mask-closable="false"
		:show-mask="false"
		resizable
		:on-update:width="handleDrawerResize"
		k-data-id="aiAgent-drawer-container">
		<n-drawer-content :closable="false">
			<template #header>
				<div class="flex items-center justify-between w-full">
					<div class="flex items-center gap-2">
						<!-- Alignment buttons -->
						<n-button
							text
							size="small"
							@click="setDrawerAlignment('left')"
							:type="drawerPlacement === 'left' ? 'primary' : 'default'"
							title="Align left">
							<PhTextAlignLeft :size="16" />
						</n-button>
						<n-button
							text
							size="small"
							@click="setDrawerAlignment('right')"
							:type="drawerPlacement === 'right' ? 'primary' : 'default'"
							title="Align right">
							<PhTextAlignRight :size="16" />
						</n-button>
						<span class="ml-2">Pinned Instructions</span>
					</div>
					<div class="flex items-center gap-2">
						<n-button text size="small" @click="handleDrawerMinimize" title="Minimize">
							<span class="text-xl">−</span>
						</n-button>
						<n-button text size="small" @click="drawerOpen = false" title="Close">
							<span class="text-xl">×</span>
						</n-button>
					</div>
				</div>
			</template>

			<div
				v-if="pinnedMessage"
				class="pinned-message-content"
				ref="drawerContent"
				k-data-id="aiAgent-drawer-pinnedContent">
				<!-- Helper note -->
				<div
					class="text-xs text-white mb-4 p-3 bg-orange-600 rounded-lg border border-theme-border"
					k-data-id="aiAgent-drawer-helperNote">
					<strong>KAi Note:</strong>
					You can resize and justify this drawer left or right to view potentially blocked elements
				</div>
				<div
					class="text-sm whitespace-pre-wrap linkified-content"
					v-html="linkifyContent(pinnedMessage.content)"></div>
			</div>
			<div v-else class="text-center text-theme-muted py-8" k-data-id="aiAgent-drawer-emptyState">
				<p>No message pinned</p>
				<p class="text-xs mt-2">Pin a message from the chat to keep it visible here</p>
			</div>
		</n-drawer-content>
	</n-drawer>
</template>

<script>
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import {
	NModal,
	NButton,
	NBadge,
	NCard,
	NInput,
	NSpin,
	NDropdown,
	NDrawer,
	NDrawerContent,
	useMessage
} from 'naive-ui'
import { PhTextAlignLeft, PhTextAlignRight, PhInfo } from '@phosphor-icons/vue'
import AIAgentService from '@utils/AIAgentService'
import { log } from '@reusables/logger'

export default {
	name: 'AIAgentChat',
	components: {
		NModal,
		NButton,
		NBadge,
		NCard,
		NInput,
		NSpin,
		NDropdown,
		NDrawer,
		NDrawerContent,
		PhTextAlignLeft,
		PhTextAlignRight,
		PhInfo
	},
	setup() {
		const store = useStore()
		const router = useRouter()
		const message = useMessage()
		const messagesContainer = ref(null)
		const drawerContent = ref(null)
		const inputMessage = ref('')
		const selectedEntity = ref(null)
		const drawerWidth = ref(400) // Resizable drawer width

		const isVisible = computed({
			get: () => store.getters['aiAgent/isAgentOpen'],
			set: (value) => {
				if (value) {
					store.dispatch('aiAgent/openAgent')
				} else {
					store.dispatch('aiAgent/closeAgent')
				}
			}
		})

		const messages = computed(() => store.getters['aiAgent/messages'])
		const isProcessing = computed(() => store.getters['aiAgent/isProcessing'])
		const isMinimized = computed(() => store.getters['aiAgent/isMinimized'])
		const selectedMode = computed(() => store.getters['aiAgent/selectedMode'])
		const drawerOpen = computed({
			get: () => store.getters['aiAgent/drawerOpen'],
			set: (value) => {
				if (value) {
					store.dispatch('aiAgent/openDrawer')
				} else {
					store.dispatch('aiAgent/closeDrawer')
				}
			}
		})
		const drawerMinimized = computed(() => store.getters['aiAgent/drawerMinimized'])
		const pinnedMessage = computed(() => store.getters['aiAgent/pinnedMessage'])
		const drawerPlacement = computed(() => store.getters['aiAgent/drawerPlacement'])

		const handleMinimize = () => {
			store.dispatch('aiAgent/minimizeAgent')
		}

		const handleMaximize = () => {
			store.dispatch('aiAgent/maximizeAgent')
		}

		const handleDrawerMinimize = () => {
			store.dispatch('aiAgent/minimizeDrawer')
		}

		const handleDrawerMaximize = () => {
			store.dispatch('aiAgent/maximizeDrawer')
		}

		const handlePinMessage = (message) => {
			store.dispatch('aiAgent/openDrawer', message)
			log('Pinned message to drawer:', message)
		}

		const handleDrawerResize = (width) => {
			drawerWidth.value = width
		}

		const setDrawerAlignment = (placement) => {
			store.dispatch('aiAgent/setDrawerPlacement', placement)
			log('Drawer alignment changed to:', placement)
		}

		const scrollToBottom = () => {
			nextTick(() => {
				if (messagesContainer.value) {
					messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
				}
			})
		}

		watch(
			messages,
			() => {
				scrollToBottom()
			},
			{ deep: true }
		)

		watch(isVisible, (newVal) => {
			if (newVal) {
				scrollToBottom()
				// Focus input when modal opens
				nextTick(() => {
					const input = document.querySelector('.n-input__textarea-el')
					if (input) input.focus()
				})
			}
		})

		const handleEnterKey = (e) => {
			// Send on Enter, new line on Shift+Enter
			if (!e.shiftKey) {
				e.preventDefault()
				sendMessage()
			}
		}

		const sendMessage = async () => {
			const message = inputMessage.value.trim()
			if (!message) return

			log('Sending message:', message)

			// Add user message to store
			store.dispatch('aiAgent/addUserMessage', message)

			// Clear input
			inputMessage.value = ''

			// Set processing state
			store.dispatch('aiAgent/setProcessing', true)

			try {
				// Call AI agent service with mode
				const response = await AIAgentService.query(message, selectedMode.value || 'search')

				log('AI Agent response:', response)

				// Add agent response to store
				store.dispatch('aiAgent/addAgentMessage', {
					content: response.message,
					data: response.data,
					formatted: response.formatted,
					fieldData: response.fieldData,
					suggestions: response.suggestions,
					metadata: response.metadata
				})
			} catch (error) {
				log('Error processing message:', error)
				store.dispatch('aiAgent/addAgentMessage', {
					content: 'Sorry, I encountered an error processing your request. Please try again.',
					suggestions: ['Try rephrasing', 'Clear chat and start over']
				})
			} finally {
				store.dispatch('aiAgent/setProcessing', false)
			}
		}

		const handleSuggestionClick = (suggestion) => {
			inputMessage.value = suggestion
			sendMessage()
		}

		const handlePromptSuggestion = (suggestion) => {
			// Pre-fill input without sending
			inputMessage.value = suggestion

			// Focus the input
			nextTick(() => {
				const input = document.querySelector('.n-input__textarea-el')
				if (input) {
					input.focus()
					// Move cursor to end
					input.setSelectionRange(input.value.length, input.value.length)
				}
			})
		}

		const selectMode = (mode) => {
			store.dispatch('aiAgent/setSelectedMode', mode)
			log('Selected mode:', mode)
		}

		const clearChat = () => {
			store.dispatch('aiAgent/clearMessages')
			AIAgentService.clearHistory()
			message.success('Chat cleared')

			// Clear selected entity when clearing chat
			if (selectedEntity.value) {
				clearSelectedEntity()
			}
		}

		const copyToClipboard = async (text) => {
			try {
				await navigator.clipboard.writeText(text)
				message.success('Copied to clipboard!')
			} catch (error) {
				log('Error copying to clipboard:', error)
				message.error('Failed to copy to clipboard')
			}
		}

		const copyChatTranscript = () => {
			const transcript = messages.value
				.filter((msg) => msg.type !== 'system')
				.map((msg) => {
					if (msg.type === 'user') {
						return `You: ${msg.content}`
					} else {
						let text = `Assistant: ${msg.content}`

						// Add field data if available
						if (msg.fieldData) {
							text += `\n\n${msg.fieldData.field}: ${typeof msg.fieldData.value === 'object' ? JSON.stringify(msg.fieldData.value, null, 2) : msg.fieldData.value}`
						}

						// Add formatted data if available
						if (msg.formatted) {
							text += '\n\nResults:'
							if (Array.isArray(msg.formatted)) {
								msg.formatted.forEach((item, idx) => {
									text += `\n${idx + 1}. ${item.name} (${item.email}) - ID: ${item.id}`
								})
							} else {
								text += `\n- ${msg.formatted.name} (${msg.formatted.email}) - ID: ${msg.formatted.id}`
							}
						}

						return text
					}
				})
				.join('\n\n---\n\n')

			copyToClipboard(transcript)
		}

		const copyFullData = () => {
			const fullData = {
				timestamp: new Date().toISOString(),
				messageCount: messages.value.length,
				conversationHistory: AIAgentService.getConversationHistory(),
				messages: messages.value.map((msg) => ({
					...msg,
					timestamp: msg.timestamp?.toISOString?.() || msg.timestamp
				})),
				context: AIAgentService.getContext()
			}

			copyToClipboard(JSON.stringify(fullData, null, 2))
		}

		// Dynamic entity selection - works for any entity type
		const selectEntity = (entity) => {
			selectedEntity.value = entity
			const entityName = entity._displayConfig?.entityName || 'item'
			log(`Selected ${entityName}:`, entity)
			message.success(`Selected: ${entity.name || entity.id}`)

			// Update the AIAgentService context based on entity type
			const collectionMap = {
				user: 'users',
				course: 'coursesV2'
				// Add more mappings as needed: event: 'events', webinar: 'webinars', etc.
			}

			const collection = collectionMap[entity.type] || entity.type
			AIAgentService.currentContext.lastDocumentId = entity.id
			AIAgentService.currentContext.lastCollection = collection

			// Set lastUserId only if it's a user
			if (entity.type === 'user') {
				AIAgentService.currentContext.lastUserId = entity.id
			} else {
				AIAgentService.currentContext.lastUserId = null
			}

			log('Updated AIAgent context:', {
				lastDocumentId: AIAgentService.currentContext.lastDocumentId,
				lastCollection: AIAgentService.currentContext.lastCollection,
				lastUserId: AIAgentService.currentContext.lastUserId
			})
		}

		const clearSelectedEntity = () => {
			const entityName = selectedEntity.value?._displayConfig?.entityName || 'item'
			selectedEntity.value = null
			message.info(`Cleared selected ${entityName}`)
		}

		// Get display fields from entity config
		const getDisplayFields = (entity) => {
			return entity._displayConfig?.fields || []
		}

		// Get CSS class for field based on styling flags
		const getFieldClass = (field, forSelected = false) => {
			const classes = []

			if (field.primary) {
				classes.push(
					forSelected
						? 'text-sm font-semibold text-theme-primary'
						: 'font-semibold text-sm text-theme-primary group-hover:text-blue-500 transition-colors'
				)
			} else if (field.secondary) {
				classes.push('text-xs text-theme-secondary')
			} else if (field.muted) {
				classes.push('text-xs text-theme-muted')
			} else {
				classes.push('text-xs')
			}

			return classes.join(' ')
		}

		// Get entity name from formatted data (singular)
		const getEntityName = (formatted) => {
			const item = Array.isArray(formatted) ? formatted[0] : formatted
			return item?._displayConfig?.entityName || 'item'
		}

		// Get appropriate article (a/an) for entity name
		const getArticle = (formatted) => {
			const entityName = getEntityName(formatted)
			const vowels = ['a', 'e', 'i', 'o', 'u']
			return vowels.includes(entityName[0]?.toLowerCase()) ? 'an' : 'a'
		}

		// Get dynamic input placeholder
		const getInputPlaceholder = () => {
			if (selectedEntity.value) {
				const name = selectedEntity.value.name || selectedEntity.value.id
				return `Ask about ${name}...`
			}
			return 'Ask me anything... (Cmd/Ctrl+K to toggle)'
		}

		// Get entity actions from config
		const getEntityActions = (entity) => {
			return entity._displayConfig?.actions || []
		}

		// Handle entity action (select, navigate, etc.)
		const handleEntityAction = (action, entity) => {
			if (action.action === 'select') {
				selectEntity(entity)
			} else if (action.action === 'navigate') {
				// Replace placeholders in path
				let path = action.path
				path = path.replace('{id}', entity.id)
				path = path.replace('{path}', entity.path || entity.id)

				// Navigate using Vue Router
				log('Navigating to:', path)
				window.location.href = path // Use window.location for now, can be replaced with router.push
			}
		}

		const copyOptions = [
			{
				label: 'Copy Chat Transcript',
				key: 'transcript',
				props: {
					onClick: copyChatTranscript
				}
			},
			{
				label: 'Copy Full Data (JSON)',
				key: 'full-data',
				props: {
					onClick: copyFullData
				}
			}
		]

		// Handle internal link clicks
		const handleInternalLinkClick = (path) => {
			log('Navigating to internal path:', path)
			// Minimize the chat (but keep drawer open)
			store.dispatch('aiAgent/minimizeAgent')
			// Navigate using Vue Router to avoid page reload
			router.push(path)
		}

		// Linkify URLs in text content
		const linkifyContent = (text) => {
			if (!text) return ''

			// Escape HTML to prevent XSS
			const escapeHtml = (str) => {
				const div = document.createElement('div')
				div.textContent = str
				return div.innerHTML
			}

			const escapedText = escapeHtml(text)

			// Convert internal URLs (starting with /) to clickable links with tooltip
			// Use data-internal-path attribute to handle clicks properly
			let linkedText = escapedText.replace(/`(\/[a-zA-Z0-9\-_/]+)`/g, (match, path) => {
				return `<span class="internal-link" data-internal-path="${path}" title="Go there now">${path}</span>`
			})

			// Also match internal URLs not in backticks but surrounded by whitespace or line start/end
			linkedText = linkedText.replace(
				/(^|\s)(\/admin\/[a-zA-Z0-9\-_/]+)(\s|$|\.)/g,
				(match, before, path, after) => {
					return `${before}<span class="internal-link" data-internal-path="${path}" title="Go there now">${path}</span>${after}`
				}
			)

			// Convert external URLs to clickable links
			const urlRegex = /(https?:\/\/[^\s]+)/g
			linkedText = linkedText.replace(urlRegex, (url) => {
				return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="external-link">${url}</a>`
			})

			// Convert **bold** markdown to <strong>
			const boldText = linkedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')

			// Preserve line breaks
			return boldText.replace(/\n/g, '<br>')
		}

		// Add click event listener for internal links (shared handler function)
		const handleLinkClick = (e) => {
			const target = e.target
			if (target.classList.contains('internal-link')) {
				e.preventDefault()
				const path = target.getAttribute('data-internal-path')
				if (path) {
					handleInternalLinkClick(path)
				}
			}
		}

		// Add click event listener for internal links in messages container
		const setupInternalLinkHandlers = () => {
			nextTick(() => {
				const container = messagesContainer.value
				if (!container) return
				// Remove existing listener before adding new one
				container.removeEventListener('click', handleLinkClick)
				container.addEventListener('click', handleLinkClick)
			})
		}

		// Add click event listener for internal links in drawer
		const setupDrawerLinkHandlers = () => {
			nextTick(() => {
				const drawer = drawerContent.value
				if (!drawer) return
				// Remove existing listener before adding new one
				drawer.removeEventListener('click', handleLinkClick)
				drawer.addEventListener('click', handleLinkClick)
			})
		}

		// Cleanup event listeners on unmount
		onBeforeUnmount(() => {
			if (messagesContainer.value) {
				messagesContainer.value.removeEventListener('click', handleLinkClick)
			}
			if (drawerContent.value) {
				drawerContent.value.removeEventListener('click', handleLinkClick)
			}
		})

		// Setup handlers on mount
		watch(messagesContainer, (newVal) => {
			if (newVal) {
				setupInternalLinkHandlers()
			}
		})

		// Setup drawer handlers when drawer content appears
		watch(drawerContent, (newVal) => {
			if (newVal) {
				setupDrawerLinkHandlers()
			}
		})

		// Also watch pinnedMessage to re-setup handlers when content changes
		watch(pinnedMessage, () => {
			setupDrawerLinkHandlers()
		})

		// On mount, check if we should show minimized badge
		onMounted(() => {
			// If there are persisted messages and it was minimized, show as open+minimized
			if (messages.value.length > 0 && isMinimized.value) {
				store.dispatch('aiAgent/openAgent')
				store.dispatch('aiAgent/minimizeAgent')
			}

			// If messages exist but no mode selected, default to search mode
			// This handles the case where user had old chats before mode feature was added
			if (messages.value.length > 0 && !selectedMode.value) {
				store.dispatch('aiAgent/setSelectedMode', 'search')
			}
		})

		return {
			isVisible,
			messages,
			isProcessing,
			isMinimized,
			messagesContainer,
			drawerContent,
			inputMessage,
			selectedEntity,
			selectedMode,
			selectMode,
			handleMinimize,
			handleMaximize,
			drawerOpen,
			drawerMinimized,
			pinnedMessage,
			drawerWidth,
			drawerPlacement,
			handlePinMessage,
			handleDrawerMinimize,
			handleDrawerMaximize,
			handleDrawerResize,
			setDrawerAlignment,
			sendMessage,
			handleSuggestionClick,
			handlePromptSuggestion,
			clearChat,
			handleEnterKey,
			copyOptions,
			copyChatTranscript,
			copyFullData,
			selectEntity,
			clearSelectedEntity,
			getDisplayFields,
			getFieldClass,
			getEntityName,
			getArticle,
			getInputPlaceholder,
			getEntityActions,
			handleEntityAction,
			linkifyContent
		}
	}
}
</script>

<style scoped>
.message-item {
	animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
	from {
		opacity: 0;
		transform: translateY(10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

:deep(.n-card) {
	background: var(--bg-primary);
}

:deep(.n-input__textarea-el) {
	border-radius: 12px;
}

.linkified-content :deep(.external-link) {
	color: #3b82f6;
	text-decoration: underline;
	transition: color 0.2s;
}

.linkified-content :deep(.external-link:hover) {
	color: #2563eb;
}

.linkified-content :deep(.internal-link) {
	color: #fe5f31;
	text-decoration: underline;
	font-weight: 500;
	transition: all 0.2s;
	cursor: pointer;
}

.linkified-content :deep(.internal-link:hover) {
	color: #ff4411;
	text-decoration: underline;
}

.linkified-content :deep(strong) {
	font-weight: 600;
}

/* Minimized Badge */
.kai-minimized-badge {
	position: fixed;
	top: 15px;
	left: 50%;
	transform: translateX(-50%);
	width: 32px;
	height: 32px;
	background: #fe5f31;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	box-shadow: 0 2px 8px rgba(254, 95, 49, 0.4);
	transition: all 0.3s ease;
	z-index: 9999;
	animation: slideIn 0.3s ease-out;
}

/* When drawer badge exists, shift KAi badge to the right */
.kai-minimized-badge.has-drawer-badge {
	transform: translateX(calc(-50% + 20px));
}

.kai-minimized-badge:hover {
	transform: translateX(-50%) scale(1.1);
	box-shadow: 0 4px 12px rgba(254, 95, 49, 0.5);
}

.kai-minimized-badge.has-drawer-badge:hover {
	transform: translateX(calc(-50% + 20px)) scale(1.1);
}

.kai-badge-text {
	font-size: 12px;
	font-weight: 700;
	color: #ffffff;
	user-select: none;
}

@keyframes slideIn {
	from {
		opacity: 0;
		transform: translateY(-20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

/* Minimized Drawer Badge */
.kai-drawer-minimized-badge {
	position: fixed;
	top: 15px;
	left: 50%;
	transform: translateX(-50%);
	width: 32px;
	height: 32px;
	background: #2563eb;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
	transition: all 0.3s ease;
	z-index: 9998;
	animation: slideIn 0.3s ease-out;
}

/* When KAi badge exists, shift drawer badge to the left */
.kai-drawer-minimized-badge.has-kai-badge {
	transform: translateX(calc(-50% - 20px));
}

.kai-drawer-minimized-badge:hover {
	transform: translateX(-50%) scale(1.1);
	box-shadow: 0 4px 12px rgba(16, 185, 129, 0.5);
}

.kai-drawer-minimized-badge.has-kai-badge:hover {
	transform: translateX(calc(-50% - 20px)) scale(1.1);
}
</style>
